import React, { useState, useEffect, useRef } from 'react'
import { colors } from './fieldTheme'
import { TOUR_STATES, pitchState, stateCounts, suggestNext, matchesQuery, orderedPitches, zoneOf, zoneLabel } from './fieldTours'
import { ChevronLeft, ChevronRight, CheckIcon, SearchIcon, ClearIcon } from './FieldIcons'
import { visuallyHidden, PrimaryButton } from './FieldUI'

// Fin d'un emplacement du tour (retour d'Alexandra du 24/10/2026) : écran de confirmation, comme
// la clôture d'une intervention, puis choix du prochain lieu. C'est la personne qui choisit :
// le plan du camping ne suit pas forcément les numéros. Recherche, puis la suite de la zone.

const UNDO_SECONDS = 8
const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"
const MAX_RESULTS = 30

// Confirmation plein écran, comme la clôture d'une intervention : rien ne part tout seul, la personne
// touche « Choisir le prochain emplacement » (retour d'Alexandra du 24/10/2026). undo (Rien à signaler) :
// « Annuler (8 s) » avec décompte, puis le bouton disparaît ; sinon « Revenir à <emplacement> ».
export function TourDoneScreen({ pitch, text, undo, onContinue, onBack })
{
	const [seconds, setSeconds] = useState(undo ? UNDO_SECONDS : 0)

	useEffect(() => {
		if (!undo)
			return ;
		const interval = setInterval(() => {
			setSeconds(previous => {
				if (previous <= 1)
				{
					clearInterval(interval)
					return 0
				}
				return previous - 1
			})
		}, 1000)
		return () => clearInterval(interval)
	}, [undo])

	return (
		<div role="status" style={{ position: 'fixed', inset: 0, zIndex: 30, background: '#15803D', color: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 32, paddingBottom: 'max(32px, env(safe-area-inset-bottom))', textAlign: 'center' }}>
			<span aria-hidden="true" className="field-pop" style={{ width: 104, height: 104, borderRadius: 999, background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
				<span style={{ width: 72, height: 72, borderRadius: 999, background: '#FFFFFF', color: '#15803D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<CheckIcon size={44} />
				</span>
			</span>
			<h1 className="field-rise" style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, animationDelay: '140ms' }}>{pitch.location + ' terminé'}</h1>
			{text && <p className="field-rise" style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.4, maxWidth: 320, animationDelay: '240ms' }}>{text}</p>}
			<div className="field-rise" style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24, animationDelay: '360ms' }}>
				<button
					type="button"
					className="field-solid"
					onClick={onContinue}
					style={{ backgroundImage: 'none', backgroundColor: '#FFFFFF', minHeight: 64, borderRadius: 999, border: 0, color: '#14532D', fontSize: 19, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer', padding: '6px 16px' }}
				>
					Choisir le prochain emplacement
				</button>
				{undo && seconds > 0 && (
					<button
						type="button"
						className="field-solid"
						onClick={() => Promise.resolve(undo()).then(onBack, onBack)}
						style={{ backgroundImage: 'none', backgroundColor: 'rgba(6,46,22,.35)', minHeight: 56, borderRadius: 999, border: 0, color: '#FFFFFF', fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', padding: '6px 16px', fontVariantNumeric: 'tabular-nums' }}
					>
						{'Annuler (' + seconds + ' s)'}
					</button>
				)}
				{!undo && (
					<button
						type="button"
						className="field-solid"
						onClick={onBack}
						style={{ backgroundImage: 'none', backgroundColor: 'rgba(6,46,22,.35)', minHeight: 56, borderRadius: 999, border: 0, color: '#FFFFFF', fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', padding: '6px 16px' }}
					>
						{'Revenir à ' + pitch.location}
					</button>
				)}
			</div>

		</div>
	)
}

// Ligne compacte façon liste native : tuile du numéro, une ligne de texte tronquée, détail en gris.
// L'état n'est écrit que s'il n'est pas « Pas encore fait » (les suggestions le sont toutes).
// selected : la ligne choisie reste enfoncée (bleu nuit, coche, « Choisi ») jusqu'à « Aller à … »
function PitchRow({ pitch, tour, selected, onPick })
{
	const state = pitchState(tour, pitch.location)
	const look = TOUR_STATES[state]
	const zone = zoneOf(pitch)
	const category = (pitch.location_category || '').trim()
	const details = [zone ? zoneLabel(zone) : null, state !== 'todo' ? look.label : null].filter(Boolean).join(', ')
	return (
		<button
			type="button"
			className="field-row field-row-choice"
			aria-label={pitch.location + ', ' + look.label}
			aria-pressed={selected}
			onClick={() => onPick(pitch)}
			style={{ backgroundImage: 'none', backgroundColor: selected ? colors.primary : colors.surface, width: '100%', minHeight: 64, display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px 10px 16px', border: 0, color: selected ? '#FFFFFF' : colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
		>
			<span style={{ width: 64, height: 44, borderRadius: 10, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: look.background, color: look.color, boxShadow: state === 'todo' ? 'inset 0 0 0 1px #94A3B8' : 'none', fontFamily: MONO, fontSize: 16, fontWeight: 700 }}>{pitch.location}</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 17, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{category || pitch.location}</span>
				{(selected || details) && <span style={{ fontSize: 15, fontWeight: selected ? 700 : 500, color: selected ? '#FFFFFF' : colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected ? 'Choisi' : details}</span>}
			</span>
			{selected ? <CheckIcon size={26} color="#FFFFFF" /> : <ChevronRight />}
		</button>
	)
}

function Rows({ children })
{
	const items = React.Children.toArray(children)
	return (
		<div style={{ background: colors.surface, borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
			{/* Filet décalé après la tuile, comme une liste iOS : seul le trait est décalé, pas la ligne */}
			{items.map((child, index) => (
				<React.Fragment key={child.key || index}>
					{index > 0 && <span aria-hidden="true" style={{ display: 'block', height: 1, background: colors.separator, marginLeft: 94 }} />}
					{child}
				</React.Fragment>
			))}
		</div>
	)
}

const sectionTitle = { fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', margin: '20px 4px 8px' }
const TITLE = 'Où allez-vous maintenant ?'

// Prochain lieu, à la manière des listes iOS (retour d'Alexandra du 24/10/2026) : grand titre dans la page
// qui passe en petit dans la barre au défilement, recherche épinglée sous la barre, lignes compactes.
export function TourNextScreen({ tour, pitch, pitches, onPick, onBack })
{
	const [query, setQuery] = useState('')
	const [compact, setCompact] = useState(false)
	const [barHeight, setBarHeight] = useState(0)
	const [chosen, setChosen] = useState(null)
	const barRef = useRef(null)
	const titleRef = useRef(null)
	const counts = stateCounts(pitches, tour)
	const doneTotal = pitches.length - counts.todo
	const searching = query.trim() !== ''
	const suggestion = suggestNext(pitches, tour, pitch.location)
	const nearby = suggestion.pitches
	const currentZone = zoneOf(pitch)
	const found = searching ? orderedPitches(pitches.filter(item => matchesQuery(item, query))) : []
	const results = found.filter(item => pitchState(tour, item.location) === 'todo').concat(found.filter(item => pitchState(tour, item.location) !== 'todo')).slice(0, MAX_RESULTS)

	useEffect(() => {
		const scroller = document.querySelector('.field-app')
		if (scroller)
			scroller.scrollTop = 0
	}, [])

	// Toucher choisit (ou change, ou retire le choix) ; rien ne s'ouvre avant « Aller à … »
	const pick = (item) => setChosen(current => (current && current.location === item.location ? null : item))
	const rowProps = (item) => ({ selected: !!chosen && chosen.location === item.location, onPick: pick })

	// Hauteur de la barre (encoche comprise) : la recherche s'épingle juste dessous
	useEffect(() => {
		const measure = () => {
			if (barRef.current)
				setBarHeight(barRef.current.offsetHeight)
		}
		measure()
		window.addEventListener('resize', measure)
		return () => window.removeEventListener('resize', measure)
	}, [])

	// Le petit titre apparaît dans la barre quand le grand titre est passé dessous
	useEffect(() => {
		const scroller = document.querySelector('.field-app')
		if (!scroller || !titleRef.current)
			return ;
		const update = () => {
			const title = titleRef.current
			if (title && barRef.current)
				setCompact(title.getBoundingClientRect().bottom <= barRef.current.getBoundingClientRect().bottom)
		}
		scroller.addEventListener('scroll', update, { passive: true })
		return () => scroller.removeEventListener('scroll', update)
	}, [])

	return (
		<div className="field-screen" style={{ flex: 1, display: 'flex', flexDirection: 'column', marginTop: 'calc(-1 * max(12px, env(safe-area-inset-top)))' }}>
			<div ref={barRef} style={{ position: 'sticky', top: 0, zIndex: 4, background: colors.background, paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8, minHeight: 52, padding: '0 20px 0 8px' }}>
					<button
						type="button"
						aria-label="Retour au plan du tour"
						onClick={onBack}
						style={{ backgroundImage: 'none', backgroundColor: 'transparent', justifySelf: 'start', minHeight: 48, padding: '0 12px 0 4px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, fontFamily: 'inherit', cursor: 'pointer' }}
					>
						<ChevronLeft />
						Plan
					</button>
					<span aria-hidden={!compact} style={{ fontSize: 17, fontWeight: 800, color: colors.text, opacity: compact ? 1 : 0, transition: 'opacity 150ms ease-out', whiteSpace: 'nowrap' }}>Prochain lieu</span>
					<span aria-label={doneTotal + ' emplacements faits sur ' + pitches.length} style={{ justifySelf: 'end', fontSize: 17, fontWeight: 700, color: colors.primary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{doneTotal + ' / ' + pitches.length}</span>
				</div>
				<div role="progressbar" aria-valuemin={0} aria-valuemax={pitches.length} aria-valuenow={doneTotal} aria-label="Avancement du tour" style={{ height: 3, background: '#E8EBF5' }}>
					<div style={{ height: '100%', background: colors.primary, width: (pitches.length ? Math.round(doneTotal * 1000 / pitches.length) / 10 : 0) + '%' }} />
				</div>
			</div>

			<div ref={titleRef} style={{ padding: '16px 20px 4px' }}>
				<h1 style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, color: colors.text }}>{TITLE}</h1>
			</div>

			<div style={{ position: 'sticky', top: barHeight, zIndex: 3, background: colors.background, padding: '8px 20px 10px' }}>
				<div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
					<label htmlFor="field-next-search" style={visuallyHidden}>Rechercher un numéro d'emplacement</label>
					<span aria-hidden="true" style={{ position: 'absolute', left: 14, display: 'flex' }}><SearchIcon size={20} color={colors.textSecondary} /></span>
					<input
						id="field-next-search"
						type="search"
						className="browser-default"
						value={query}
						autoComplete="off"
						placeholder="Rechercher un n° (ex : J069)"
						onChange={(e) => setQuery(e.target.value)}
						style={{ width: '100%', height: 48, margin: 0, padding: query ? '0 48px 0 44px' : '0 14px 0 44px', borderRadius: 12, border: 0, background: '#E8EBF5', fontSize: 17, fontFamily: 'inherit', color: colors.text, boxSizing: 'border-box' }}
					/>
					{query && (
						<button type="button" aria-label="Effacer la recherche" onClick={() => setQuery('')} style={{ backgroundImage: 'none', backgroundColor: 'transparent', position: 'absolute', right: 0, width: 48, height: 48, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textSecondary, cursor: 'pointer' }}>
							<ClearIcon />
						</button>
					)}
				</div>
			</div>

			<div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column' }}>
				{searching && (
					<React.Fragment>
						<h2 style={sectionTitle}>{'Résultats (' + found.length + ')'}</h2>
						{results.length === 0 && <p style={{ background: '#EEF0F5', borderRadius: 12, padding: '14px 16px', fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>{'Aucun emplacement pour « ' + query.trim() + ' »'}</p>}
						{results.length > 0 && <Rows>{results.map(item => <PitchRow key={item.location} pitch={item} tour={tour} {...rowProps(item)} />)}</Rows>}
					</React.Fragment>
				)}

				{!searching && (
					<React.Fragment>
						{!suggestion.sameZone && nearby.length > 0 && <p style={{ fontSize: 17, fontWeight: 700, color: colors.text, padding: '16px 4px 0' }}>{(currentZone ? 'Zone ' + currentZone : 'Cette zone') + ' terminée.'}</p>}
						<h2 style={sectionTitle}>{suggestion.sameZone ? (currentZone ? 'Suite de la zone ' + currentZone : 'Suite de la liste') : 'Zone suivante : ' + (suggestion.zone || 'sans zone')}</h2>
						{nearby.length === 0 && <p style={{ background: '#EEF0F5', borderRadius: 12, padding: '14px 16px', fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>Tous les emplacements sont faits.</p>}
						{nearby.length > 0 && <Rows>{nearby.map(item => <PitchRow key={item.location} pitch={item} tour={tour} {...rowProps(item)} />)}</Rows>}
						{nearby.length > 0 && <p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, padding: '8px 4px 0' }}>Pas encore faits, dans l'ordre des numéros de la zone.</p>}
					</React.Fragment>
				)}
			</div>

			<div style={{ position: 'sticky', bottom: 0, zIndex: 3, marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 20px', paddingBottom: 'max(20px, env(safe-area-inset-bottom))', background: colors.background, borderTop: '1px solid ' + colors.separator }}>
				<PrimaryButton disabled={!chosen} onClick={() => onPick(chosen)}>{chosen ? 'Aller à ' + chosen.location : 'Aller à l\'emplacement'}</PrimaryButton>
				{!chosen && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>Choisissez un emplacement</p>}
			</div>
		</div>
	)
}
